import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of, BehaviorSubject } from 'rxjs';
import { catchError, finalize, map, shareReplay, tap } from 'rxjs/operators';

import { environment } from '../../environments/environment';
import { TokenStorageService } from '@services/token-storage.service';



@Injectable({
  providedIn: 'root'
})
export class TeamService {
  readonly defaultAvatarUrl = 'assets/images/avatars/default-user.png';
  private readonly teamAvatarCache = new Map<string, string>();
  private readonly teamAvatarInflight = new Map<string, Observable<string>>();
  private readonly teamBlobUrls = new Map<string, string>();

  constructor(private http: HttpClient) { }

  private getHeaders(): HttpHeaders {
    const token = TokenStorageService.getToken();
    return new HttpHeaders().set('Authorization', `Bearer ${token}`);
  }

  private hasToken(): boolean {
    const token = TokenStorageService.getToken();
    if (!token) {
      console.log('No token found in localStorage');
      return false;
    }
    return true;
  }

  get_user_team_roles(team_code: string): Observable<any> {
    if (!this.hasToken()) return of(null);
    return this.http.post(`${environment.apiBaseUrl}/team/get_user_team_roles`, { team_code: team_code }, { headers: this.getHeaders() });
  }

  get_team_info(team_code: string): Observable<any> {
    if (!this.hasToken()) return of(null);
    return this.http.post(`${environment.apiBaseUrl}/team/get_team_info`, { team_code: team_code }, { headers: this.getHeaders() });
  }

  update_team_info(team_code: string, team: any, notification: boolean): Observable<any> {
    if (!this.hasToken()) return of(null);
    return this.http.post(`${environment.apiBaseUrl}/team/update_team_info`, { team_code: team_code, team: team, notification: notification }, { headers: this.getHeaders() });
  }

  update_team_notification(team_code: string, notification: boolean): Observable<any> {
    if (!this.hasToken()) return of(null);
    return this.http.post(`${environment.apiBaseUrl}/team/update_team_notification`, { team_code: team_code, notification: notification }, { headers: this.getHeaders() });
  }

  reply_entering_application(team_code: string, applicant: string | { email: string }, reply: boolean): Observable<any> {
    if (!this.hasToken()) return of(null);
    const applicantPayload =
      typeof applicant === 'string' ? { email: applicant } : { email: applicant.email };
    return this.http.post(`${environment.apiBaseUrl}/team/reply_entering_application`, { team_code: team_code, applicant: applicantPayload, reply: reply }, { headers: this.getHeaders() });
  }

  remove_member(team_code: string, member: string): Observable<any> {
    if (!this.hasToken()) return of(null);
    return this.http.post(`${environment.apiBaseUrl}/team/remove_member`, { team_code: team_code, member: member }, { headers: this.getHeaders() });
  }

  edit_member_auth(team_code: string, applicant: string, roles: string[]): Observable<any> {
    if (!this.hasToken()) return of(null);
    return this.http.post(`${environment.apiBaseUrl}/team/edit_member_auth`, { team_code, applicant: applicant, roles: roles }, { headers: this.getHeaders() });
  }

  check_enterprise_payment(team_code: string): Observable<any> {
    if (!this.hasToken()) return of(null);
    return this.http.post(`${environment.apiBaseUrl}/team/check_enterprise_payment`, { team_code }, { headers: this.getHeaders() });
  }

  check_enterprise_payout(team_code: string): Observable<any> {
    if (!this.hasToken()) return of(null);
    return this.http.post(`${environment.apiBaseUrl}/team/check_enterprise_payout`, { team_code }, { headers: this.getHeaders() });
  }

  activate_application(team_code: string, appId: number): Observable<any> {
    if (!this.hasToken()) return of(null);
    return this.http.post(`${environment.apiBaseUrl}/team/activate_application`, { team_code, appId }, { headers: this.getHeaders() });
  }

  getTeamEvents(team_code: string, requestData?: any): Observable<any> {
    if (!this.hasToken()) return of(null);
    const data = { team_code: team_code, ...(requestData || {}) };
    return this.http.post(`${environment.apiBaseUrl}/team/get_events`, data, { headers: this.getHeaders() });
  }

  getTeamEventsAll(team_code: string): Observable<any> {
    if (!this.hasToken()) return of(null);
    return this.http.post(`${environment.apiBaseUrl}/team/get_events_all`, { team_code: team_code }, { headers: this.getHeaders() });
  }

  // ==================== TERMS OF USE API ====================

  get_latest_terms(lang: string): Observable<any> {
    if (!this.hasToken()) return of(null);
    return this.http.get(`${environment.apiBaseUrl}/api/terms/get_latest?lang=${lang}`, { headers: this.getHeaders() });
  }

  check_acceptance_status(): Observable<any> {
    if (!this.hasToken()) return of(null);
    return this.http.get(`${environment.apiBaseUrl}/api/terms/check_acceptance_status`, { headers: this.getHeaders() });
  }

  accept_terms(version: string, lang: string): Observable<any> {
    if (!this.hasToken()) return of(null);
    return this.http.post(`${environment.apiBaseUrl}/api/terms/accept_terms`, { version, lang }, { headers: this.getHeaders() });
  }

  getTeamAvatar(team_code: string): Observable<any> {
    if (!this.hasToken()) return of(null);
    return this.http.post(`${environment.apiBaseUrl}/team/get-team-avatar`, { team_code: team_code }, { headers: this.getHeaders(), responseType: 'blob' });
  }

  /** 載入團隊頭像 URL（含快取、空 blob / 錯誤回退預設圖） */
  loadTeamAvatarUrl(teamCode: string, force = false): Observable<string> {
    const code = (teamCode || '').trim();
    if (!code || !this.hasToken()) {
      return of(this.defaultAvatarUrl);
    }
    if (!force && this.teamAvatarCache.has(code)) {
      return of(this.teamAvatarCache.get(code)!);
    }
    const inflight = this.teamAvatarInflight.get(code);
    if (!force && inflight) {
      return inflight;
    }
    const request$ = this.http
      .post(`${environment.apiBaseUrl}/team/get-team-avatar`, { team_code: code }, {
        headers: this.getHeaders(),
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(
        map((res) => this.resolveTeamAvatarBlob(res.body, res.status)),
        catchError(() => of(this.defaultAvatarUrl)),
        tap((url) => this.setTeamAvatarUrl(code, url)),
        finalize(() => {
          this.teamAvatarInflight.delete(code);
        }),
        shareReplay(1),
      );
    this.teamAvatarInflight.set(code, request$);
    return request$;
  }

  private resolveTeamAvatarBlob(body: Blob | null | undefined, status: number): string {
    const ok = status >= 200 && status < 300 && status !== 204;
    if (!ok || !body || body.size === 0) {
      return this.defaultAvatarUrl;
    }
    const type = (body.type || '').toLowerCase();
    if (type.includes('json') || type.includes('text/html') || type.includes('text/plain')) {
      return this.defaultAvatarUrl;
    }
    return URL.createObjectURL(body);
  }

  private setTeamAvatarUrl(teamCode: string, url: string): void {
    const prev = this.teamBlobUrls.get(teamCode);
    if (prev && prev.startsWith('blob:') && prev !== url) {
      URL.revokeObjectURL(prev);
    }
    if (url.startsWith('blob:')) {
      this.teamBlobUrls.set(teamCode, url);
    } else {
      this.teamBlobUrls.delete(teamCode);
    }
    this.teamAvatarCache.set(teamCode, url);
  }

  update_team_avatar(team_code: string, file: File): Observable<any> {
    if (!this.hasToken()) return of(null);
    console.log(file);
    const formData = new FormData();
    formData.append('team_code', team_code);
    console.log(formData);
    formData.append('profile_picture', file);
    return this.http.post(`${environment.apiBaseUrl}/team/update_team_avatar`, formData, { headers: this.getHeaders() });
  }

  getUserByEmail(email: string, team_code: string): Observable<any> {
    if (!this.hasToken() || !email || !team_code) return of(null);
    return this.http.post(`${environment.apiBaseUrl}/team/get-user-by-email`, {
      email: email,
      team_code: team_code
    }, { headers: this.getHeaders() });
  }

  get_manager_approval_events(team_code: string, approval_status: 'pending' | 'approved' | 'rejected' | 'all' = 'all'): Observable<any> {
    if (!this.hasToken()) return of(null);
    return this.http.post(`${environment.apiBaseUrl}/team/get_manager_approval_events`, { team_code, approval_status }, { headers: this.getHeaders() });
  }

  update_manager_approval(team_code: string, event_id: string, approval: boolean): Observable<any> {
    if (!this.hasToken()) return of(null);
    return this.http.post(`${environment.apiBaseUrl}/team/update_manager_approval`, { team_code, event_id, approval }, { headers: this.getHeaders() });
  }

  // ==================== BILLING & INVOICE API ====================

  /**
   * 取得團隊帳務列表 (包含未結算帳款、已結算等，可透過 status 篩選)
   * 權限：團隊成員
   * API: /team_app/1/billing/get_issued_bills
   */
  get_issued_bills(team_code: string): Observable<any> {
    if (!this.hasToken()) return of(null);
    return this.http.post(`${environment.apiBaseUrl}/team/get_issued_bills`, { team_code }, { headers: this.getHeaders() });
  }

  /**
   * 開立發票 (結算未結帳款)
   * 權限：團隊管理員 (或財務相關權限)
   * API: /team/create_invoice
   */
  create_invoice(team_code: string, bill_ids: number[], invoice_date?: string): Observable<any> {
    if (!this.hasToken()) return of(null);
    return this.http.post(`${environment.apiBaseUrl}/team/create_invoice`, {
      team_code,
      bill_ids,
      invoice_date
    }, { headers: this.getHeaders() });
  }

  /**
   * 取得已開立發票列表 (包含發票明細項目)
   * API: /team/get_invoices
   */
  get_invoices(team_code: string, start_date?: string, end_date?: string): Observable<any> {
    if (!this.hasToken()) return of(null);
    return this.http.post(`${environment.apiBaseUrl}/team/get_invoices`, {
      team_code,
      start_date,
      end_date
    }, { headers: this.getHeaders() });
  }

  discover_equipment_vendors(team_code: string, limit = 100): Observable<any> {
    if (!this.hasToken()) return of(null);
    return this.http.post(`${environment.apiBaseUrl}/team/discover/equipment_vendors`, {
      team_code,
      limit,
    }, { headers: this.getHeaders() });
  }

  discover_suppliers(team_code: string, limit = 100): Observable<any> {
    if (!this.hasToken()) return of(null);
    return this.http.post(`${environment.apiBaseUrl}/team/discover/suppliers`, {
      team_code,
      limit,
    }, { headers: this.getHeaders() });
  }

  get_legal_entity_payment_capabilities(team_code: string, taxId?: string): Observable<any> {
    if (!this.hasToken()) return of(null);
    return this.http.post(`${environment.apiBaseUrl}/team/legal_entity/payment_capabilities`, {
      team_code,
      taxId,
    }, { headers: this.getHeaders() });
  }

  verify_legal_entity_tax_id(team_code: string, taxId: string, name?: string): Observable<any> {
    if (!this.hasToken()) return of(null);
    return this.http.post(`${environment.apiBaseUrl}/team/legal_entity/verify_tax_id`, {
      team_code, taxId, name
    }, { headers: this.getHeaders() });
  }

  test_legal_entity_einvoice(
    team_code: string,
    taxId: string,
    merchant_id?: string,
    hash_key?: string,
    hash_iv?: string,
    api_base?: string,
  ): Observable<any> {
    if (!this.hasToken()) return of(null);
    return this.http.post(`${environment.apiBaseUrl}/team/legal_entity/test_einvoice`, {
      team_code,
      taxId,
      merchant_id,
      hash_key,
      hash_iv,
      api_base,
    }, { headers: this.getHeaders() });
  }

  test_legal_entity_newebpay_payment(
    team_code: string,
    taxId: string,
    merchant_id?: string,
    hash_key?: string,
    hash_iv?: string,
    api_base?: string,
  ): Observable<any> {
    if (!this.hasToken()) return of(null);
    return this.http.post(`${environment.apiBaseUrl}/team/legal_entity/test_newebpay_payment`, {
      team_code,
      taxId,
      merchant_id,
      hash_key,
      hash_iv,
      api_base,
    }, { headers: this.getHeaders() });
  }

  enable_legal_entity_newebpay_va(team_code: string, taxId: string): Observable<any> {
    if (!this.hasToken()) return of(null);
    return this.http.post(`${environment.apiBaseUrl}/team/legal_entity/enable_newebpay_va`, {
      team_code,
      taxId,
    }, { headers: this.getHeaders() });
  }

  get_legal_entity_onboarding_status(team_code: string): Observable<any> {
    if (!this.hasToken()) return of(null);
    return this.http.post(`${environment.apiBaseUrl}/team/legal_entity/onboarding_status`, {
      team_code
    }, { headers: this.getHeaders() });
  }

  complete_onboarding_step(team_code: string, taxId: string, step: string, status = 'verified'): Observable<any> {
    if (!this.hasToken()) return of(null);
    return this.http.post(`${environment.apiBaseUrl}/team/legal_entity/complete_onboarding_step`, {
      team_code, taxId, step, status
    }, { headers: this.getHeaders() });
  }

  list_platform_invoice_requests(team_code: string, status = 'pending'): Observable<any> {
    if (!this.hasToken()) return of(null);
    return this.http.post(`${environment.apiBaseUrl}/team/platform_invoice_requests/list`, {
      team_code,
      status,
    }, { headers: this.getHeaders() });
  }

  get_pending_transfers(team_code: string): Observable<any> {
    if (!this.hasToken()) return of(null);
    return this.http.post(`${environment.apiBaseUrl}/team/payment/pending_transfers`, {
      team_code
    }, { headers: this.getHeaders() });
  }

}
